import peakMermaid from '../assets/peak-mermaid.svg';

// The Peak mermaid, sitting on a wave along the bottom of the app.
//
// Purely decorative, and built so it can never cost anything:
//   • `aria-hidden` + `pointer-events: none` — invisible to screen readers and
//     to the mouse, so it can never eat a click meant for the content.
//   • It FLOWS after the content instead of being pinned to the viewport. A
//     fixed watermark sounded right and was wrong: the cards are opaque, so it
//     would be hidden on exactly the screens that have something to show, and
//     visible only on empty ones. Flowed, it closes every page — and it can
//     never sit behind a figure, which is the one thing decoration must not do
//     where people reconcile money.
//   • No animation. A background that moves while you read a table is a
//     distraction the research was explicit about; the mermaid's own float
//     animation belongs to empty states, where she IS the subject.
//
// The wave is warm sand behind and sea in front, never a grey, and the two crests
// are deliberately asymmetric — a single symmetric arc reads as a UI chrome
// element (a progress hump), while an offset swell reads as water.
//
// She sits at `inset-inline-end` — the bottom-LEFT here, where an RTL reader
// exits the page. A closing flourish belongs at the exit, not back at the edge
// you already read from: the wordmark is top-right, page titles are right, row
// starts are right, and putting her bottom-right too would stack a fourth thing
// on one rail. The bookend that actually reads is diagonal — brand opens
// top-right, brand closes bottom-left, like a signature in the far corner.
//
// LOGICAL property, not `left`: the rule is "she sits at the end of the reading
// path", which is a reading-order fact rather than a geometric one. An LTR
// European build flips her to bottom-right and stays correct for free, where a
// physical `left` would quietly become wrong the day this ships in French.
export default function PageWatermark() {
  return (
    <div aria-hidden="true" className="page-watermark">
      <svg className="page-watermark-wave" viewBox="0 0 1440 130" preserveAspectRatio="none" focusable="false">
        {/* Back swell — paler and slightly higher, so the two read as depth
            rather than as one thick band. Drawn in a deeper PARCHMENT rather
            than the nav bar's sage: it sits directly on the page, and a cool
            swell on a warm ground was the one foreign note here. The green
            front swell below stays — green on cream is the logo's own
            relationship, not a clash. */}
        <path
          d="M0 62 C 210 18, 380 96, 610 70 S 1020 20, 1240 58 S 1400 78, 1440 66 L1440 130 L0 130 Z"
          fill="var(--peak-parchment-deep)"
          opacity="0.85"
        />
        {/* Front swell — the water. No crest stroke, by choice: an outline made
            the swell read as a graphic pasted on the page rather than as a
            watermark.
            This is set FAINT on purpose — an atmospheric hint of water, not a
            depicted wave. Measured against the sand band it is ~1.04:1, which
            is below the threshold where an edge is reliably perceptible, so on
            most screens the water reads as a barely-there shift in the band
            rather than as a shape. That is the intended effect; it is not a
            value someone should "fix" by raising it. If a future change does
            need the water to read as a distinct surface, LIGHTEN THE SAND
            (--peak-parchment-deep): the water is a shade darker than the sand,
            so a lighter sand widens the gap, while raising this alpha only
            deepens a colour that is already hard to see against it. */}
        <path
          d="M0 88 C 240 52, 420 118, 660 92 S 1060 54, 1280 86 S 1410 100, 1440 92 L1440 130 L0 130 Z"
          fill="var(--peak-water)"
          opacity="0.22"
        />
      </svg>
      {/* The wave bleeds full-width, but she is placed inside a box that mirrors
          `.main-content`'s column (same max-width, same gutter tokens), so her
          edge sits on the exact vertical line the cards' edge sits on. */}
      <div className="page-watermark-inner">
        <img src={peakMermaid} alt="" className="page-watermark-mermaid" />
      </div>
    </div>
  );
}
